import * as THREE from 'three';
import { SHARED_VERTEX_SHADER, SHARED_FRAGMENT_SHADER } from '../core/ParticleSystem.js';

/**
 * Dimensional Scene - The key teaching visualization
 * Morphs particles from 1D (line) -> 2D (plane) -> 3D (space)
 * Demonstrates how AI uses vectors/embeddings to find "nearest neighbors"
 */
export class DimensionalScene {
  constructor(count = 5000) {
    this.count = count;
    this.dimensionState = -1;
    this.geometry = null;
    this.material = null;
    this.points = null;

    // Store positions for each dimensional state
    this.positionsRandom = new Float32Array(count * 3);
    this.positions1D = new Float32Array(count * 3);
    this.positions2D = new Float32Array(count * 3);
    this.positions3D = new Float32Array(count * 3);

    this.queryPoint = null;
    this.neighborLines = null;
    this.textLabels = [];
    this.group = null;

    this.init();
  }

  init() {
    this.generatePositions();
    this.createGeometry();
    this.createQueryPoint();
    this.createTextLabels();
  }

  generatePositions() {
    const clusters = [
      { center: -40, label: 'sad', color: [0.3, 0.3, 0.8] },
      { center: -25, label: 'melancholy', color: [0.4, 0.4, 0.7] },
      { center: -10, label: 'neutral', color: [0.6, 0.6, 0.6] },
      { center: 10, label: 'content', color: [0.6, 0.7, 0.5] },
      { center: 25, label: 'happy', color: [0.8, 0.8, 0.3] },
      { center: 40, label: 'joyful', color: [1.0, 0.8, 0.2] },
    ];

    this.clusterColors = new Float32Array(this.count * 3);
    this.featuredParticle = new Float32Array(this.count); // 0 = normal, 1 = sad, 2 = happy

    for (let i = 0; i < this.count; i++) {
      const i3 = i * 3;

      const clusterIndex = Math.floor(Math.random() * clusters.length);
      const cluster = clusters[clusterIndex];
      const spread = 4; // Tighter clusters

      // RANDOM: Spherical distribution
      const radius = Math.pow(Math.random(), 0.5) * 60;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      this.positionsRandom[i3] = radius * Math.sin(phi) * Math.cos(theta);
      this.positionsRandom[i3 + 1] = radius * Math.sin(phi) * Math.sin(theta);
      this.positionsRandom[i3 + 2] = radius * Math.cos(phi);

      // 1D: Horizontal line - very tight vertical spread
      const linePosition = (i / this.count) * 80 - 40;
      const verticalJitter = (Math.random() - 0.5) * 0.5;
      const depthJitter = (Math.random() - 0.5) * 0.5;
      this.positions1D[i3] = linePosition;
      this.positions1D[i3 + 1] = verticalJitter;
      this.positions1D[i3 + 2] = depthJitter;

      // 2D: Add vertical dimension
      const x2D = cluster.center + (Math.random() - 0.5) * spread;
      const energyBase = cluster.center > 0 ? (cluster.center / 40) * 20 : -(cluster.center / 40) * 10;
      const y2D = energyBase + (Math.random() - 0.5) * spread;
      this.positions2D[i3] = x2D;
      this.positions2D[i3 + 1] = y2D;
      this.positions2D[i3 + 2] = 0;

      // 3D: Add depth dimension - tighter clusters
      const z3D = (Math.random() - 0.5) * 20;
      this.positions3D[i3] = x2D + (Math.random() - 0.5) * 2;
      this.positions3D[i3 + 1] = y2D + (Math.random() - 0.5) * 2;
      this.positions3D[i3 + 2] = z3D;

      // Store cluster color
      const colorCluster = clusters[Math.floor((linePosition + 40) / 80 * clusters.length)] || cluster;
      this.clusterColors[i3] = colorCluster.color[0];
      this.clusterColors[i3 + 1] = colorCluster.color[1];
      this.clusterColors[i3 + 2] = colorCluster.color[2];

      // Mark featured particles (0 = normal, 1 = sad, 2 = happy)
      this.featuredParticle[i] = 0;
    }

      // Set first particle as "sad" (at x = -40)
      this.positions1D[0] = -40;
      this.positions1D[1] = 0;
      this.positions1D[2] = 0;
      this.clusterColors[0] = 0.3;
      this.clusterColors[1] = 0.3;
      this.clusterColors[2] = 0.8;
      this.featuredParticle[0] = 1; // sad

      // Set second particle as "happy" (at x = 40)
      this.positions1D[3] = 40;
      this.positions1D[4] = 0;
      this.positions1D[5] = 0;
      this.clusterColors[3] = 1.0;
      this.clusterColors[4] = 0.8;
      this.clusterColors[5] = 0.2;
      this.featuredParticle[1] = 2; // happy
  }

  createGeometry() {
    this.geometry = new THREE.BufferGeometry();

    const positions = this.positionsRandom.slice();
    const sizes = new Float32Array(this.count);
    const phases = new Float32Array(this.count);

    for (let i = 0; i < this.count; i++) {
      // Wide size variation - matching other scenes
      const randomVariation = 0.3 + Math.random() * 3.0;
      sizes[i] = (0.5 + Math.random() * 10.0) * randomVariation;
      phases[i] = Math.random() * Math.PI * 2;
    }

    // Make featured particles much larger (twice the size)
    sizes[0] = 30.0; // sad particle
    sizes[1] = 30.0; // happy particle

    this.geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    this.geometry.setAttribute('color', new THREE.BufferAttribute(this.clusterColors.slice(), 3));
    this.geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));
    this.geometry.setAttribute('aPhase', new THREE.BufferAttribute(phases, 1));
    this.geometry.setAttribute('aFeatured', new THREE.BufferAttribute(this.featuredParticle, 1));

    this.uniforms = {
      uTime: { value: 0 },
      uMouse: { value: new THREE.Vector2(0, 0) },
      uMouseRadius: { value: 0.2 },
      uPixelRatio: { value: Math.min(window.devicePixelRatio, 2) },
      uSize: { value: 60 },
      uDimensionState: { value: 0 }
    };

    this.material = new THREE.ShaderMaterial({
      uniforms: this.uniforms,
      vertexShader: this.getVertexShader(),
      fragmentShader: this.getFragmentShader(),
      transparent: true,
      depthTest: false,
      depthWrite: false,
      vertexColors: true
    });

    this.points = new THREE.Points(this.geometry, this.material);
  }

  createQueryPoint() {
    this.queryPoint = null;
    this.neighborLines = null;
  }

  createTextLabels() {
    const createTextSprite = (text, position, color = '#ffffff') => {
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.width = 256;
      canvas.height = 64;

      context.fillStyle = 'rgba(0, 0, 0, 0)';
      context.fillRect(0, 0, canvas.width, canvas.height);

      context.font = 'bold 36px Inter, Arial, sans-serif';
      context.fillStyle = color;
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      context.fillText(text, canvas.width / 2, canvas.height / 2);

      const texture = new THREE.CanvasTexture(canvas);
      texture.minFilter = THREE.LinearFilter;

      const spriteMaterial = new THREE.SpriteMaterial({
        map: texture,
        transparent: true,
        opacity: 0.9
      });

      const sprite = new THREE.Sprite(spriteMaterial);
      sprite.position.set(position.x, position.y, position.z);
      sprite.scale.set(20, 5, 1);

      return sprite;
    };

    const labels = [
      { text: 'sad', position: { x: -40, y: -8, z: 0 }, color: '#8888ff' },
      { text: 'happy', position: { x: 40, y: -8, z: 0 }, color: '#ffcc00' },
      { text: '0.2', position: { x: -40, y: 8, z: 0 }, color: '#888888' },
      { text: '0.8', position: { x: 40, y: 8, z: 0 }, color: '#888888' },
    ];

    labels.forEach(label => {
      const sprite = createTextSprite(label.text, label.position, label.color);
      sprite.visible = false; // Start hidden, will be shown at slide 4
      sprite.material.opacity = 0; // Start with 0 opacity
      this.textLabels.push(sprite);
    });
  }

  // Custom shader that respects dimensional state while having movement
  getVertexShader() {
    return `
      uniform float uTime;
      uniform float uPixelRatio;
      uniform float uSize;
      uniform float uDimensionState;

      attribute float size;
      attribute float aPhase;
      attribute float aFeatured; // 0 = normal, 1 = sad, 2 = happy

      varying vec3 vColor;
      varying float vAlpha;
      varying float vFeatured;

      void main() {
        vec3 pos = position;

        // Movement that respects the dimensional state
        // More movement at state 0 (early slides), minimal at 0.33 (1D line)
        // movementScale: 1.0 at state 0, 0.1 at state 0.33, increases again after
        float movementScale;
        if (uDimensionState <= 0.33) {
          movementScale = 1.0 - (uDimensionState / 0.33) * 0.9; // 1.0 -> 0.1
        } else {
          movementScale = 0.1 + ((uDimensionState - 0.33) / 0.67) * 0.5; // 0.1 -> 0.6
        }

        // Dynamic movement - strong on early slides
        float speed = 0.4;
        pos.x += sin(uTime * speed + aPhase) * 5.0 * movementScale;
        pos.y += cos(uTime * speed * 0.8 + aPhase * 1.3) * 4.0 * movementScale;
        pos.z += sin(uTime * speed * 0.6 + aPhase * 0.7) * 8.0 * movementScale;

        // Additional wave motion for more organic feel on early slides
        pos.x += cos(uTime * 0.2 + pos.y * 0.05) * 3.0 * movementScale;
        pos.y += sin(uTime * 0.25 + pos.x * 0.05) * 2.0 * movementScale;
        pos.z += sin(uTime * 0.3 + aPhase * 2.0) * 5.0 * movementScale;

        vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);

        // Size with breathing effect
        float psize = size * uSize * uPixelRatio;
        psize *= (1.0 + sin(uTime * 0.5 + aPhase) * 0.2);
        psize *= (1.0 / -mvPosition.z);

        gl_PointSize = clamp(psize, 2.0, 150.0);
        gl_Position = projectionMatrix * mvPosition;

        vColor = color;
        vFeatured = aFeatured;

        // Calculate alpha based on dimension state and featured status
        // State 0: All particles visible
        // State 0.33: Only 2 featured particles visible (others at 0.01)
        // State > 0.33: All particles fade back in immediately
        float baseAlpha = 0.85 + sin(uTime * 0.3 + aPhase) * 0.1;

        // Calculate fade factor for non-featured particles
        // 0 to 0.33: fade from 1.0 to 0.0 (particles disappear)
        // > 0.33: all particles fully visible again
        float fadeToTwo = 0.0;
        if (uDimensionState <= 0.33) {
          fadeToTwo = uDimensionState / 0.33; // 0 at start, 1 at 0.33
        }
        // After 0.33, fadeToTwo stays at 0, so all particles are visible

        if (aFeatured > 0.5) {
          // Featured particles (sad/happy) - always visible
          vAlpha = baseAlpha;
        } else {
          // Normal particles - fade to 0.01 as we approach 1D state, fully visible after
          vAlpha = mix(baseAlpha, 0.01, fadeToTwo);
        }
      }
    `;
  }

  getFragmentShader() {
    return `
      varying vec3 vColor;
      varying float vAlpha;
      varying float vFeatured;

      void main() {
        vec2 uv = gl_PointCoord - 0.5;
        float dist = length(uv);
        float alpha = 1.0 - smoothstep(0.3, 0.5, dist);
        alpha *= vAlpha;

        // Discard nearly invisible pixels for performance
        if (alpha < 0.005) discard;

        gl_FragColor = vec4(vColor, alpha);
      }
    `;
  }

  setDimensionState(state) {
    state = Math.max(0, Math.min(1, state));
    if (state === this.dimensionState) return;
    this.dimensionState = state;

    const positions = this.geometry.attributes.position.array;

    for (let i = 0; i < this.count; i++) {
      const i3 = i * 3;

      if (state <= 0.33) {
        const t = state / 0.33;
        const easeT = t * t * (3 - 2 * t);
        positions[i3] = this.positionsRandom[i3] + (this.positions1D[i3] - this.positionsRandom[i3]) * easeT;
        positions[i3 + 1] = this.positionsRandom[i3 + 1] + (this.positions1D[i3 + 1] - this.positionsRandom[i3 + 1]) * easeT;
        positions[i3 + 2] = this.positionsRandom[i3 + 2] * (1 - easeT);
      } else if (state <= 0.66) {
        const t = (state - 0.33) / 0.33;
        const easeT = t * t * (3 - 2 * t);
        positions[i3] = this.positions1D[i3] + (this.positions2D[i3] - this.positions1D[i3]) * easeT;
        positions[i3 + 1] = this.positions1D[i3 + 1] + (this.positions2D[i3 + 1] - this.positions1D[i3 + 1]) * easeT;
        positions[i3 + 2] = 0;
      } else {
        const t = (state - 0.66) / 0.34;
        const easeT = t * t * (3 - 2 * t);
        positions[i3] = this.positions2D[i3] + (this.positions3D[i3] - this.positions2D[i3]) * easeT;
        positions[i3 + 1] = this.positions2D[i3 + 1] + (this.positions3D[i3 + 1] - this.positions2D[i3 + 1]) * easeT;
        positions[i3 + 2] = this.positions3D[i3 + 2] * easeT;
      }
    }

    this.geometry.attributes.position.needsUpdate = true;
    this.uniforms.uDimensionState.value = state;

    // Labels only visible when at 1D state (0.33) - slide 4
    // Show labels starting at slide 4 (state 0.33), fade out as we leave
    let labelOpacity = 0;
    if (state >= 0.32 && state <= 0.45) {
      if (state <= 0.33) {
        // Quick fade in from 0.32 to 0.33
        labelOpacity = (state - 0.32) / 0.01;
      } else if (state <= 0.40) {
        // Stay fully visible from 0.33 to 0.40
        labelOpacity = 1.0;
      } else {
        // Fade out from 0.40 to 0.45
        labelOpacity = Math.max(0, 1 - (state - 0.40) / 0.05);
      }
    }
    this.textLabels.forEach(sprite => {
      sprite.material.opacity = labelOpacity * 0.9;
      sprite.visible = labelOpacity > 0.05;
    });
  }

  update(time, deltaTime) {
    this.uniforms.uTime.value = time;
  }

  updateMouse(x, y) {
    this.uniforms.uMouse.value.set(x, y);
  }

  getMesh() {
    if (!this.group) {
      this.group = new THREE.Group();
      this.group.add(this.points);
      this.textLabels.forEach(sprite => this.group.add(sprite));
    }
    return this.group;
  }

  getPoints() {
    return this.points;
  }

  dispose() {
    if (this.geometry) this.geometry.dispose();
    if (this.material) this.material.dispose();
  }
}
